import { FC, useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router";

import { Modal } from "capitalrx-components-2.0/lib/general";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { showIdleTimerWithSecondsLeft, userTimeout } from "@/App/constants/constants";
import { AuthenticationContext, ExperienceContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { clearLocalSSOStorage } from "@/App/service/sso";


export const AutoLogout: FC = () => {
  const [showIdleTimerModal, setShowIdleTimerModal] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>(userTimeout);
  const navigateClient = useNavigateClient();
  const navigate = useNavigate();
  const { logoutUser, setAuthState } = useContext(AuthenticationContext);
  const { loginRedirectUrl } = useContext(ExperienceContext);
  const { t: translate } = useTranslation();

  const onIdle = () => {
    handleLogout();
  };

  const onActive = () => {
    setShowIdleTimerModal(false);
  };

  const { getRemainingTime } = useIdleTimer({
    onIdle,
    onActive,
    name: "appIdleLogoutTimer",
    timeout: userTimeout,
    events: ["mousedown", "keydown", "touchstart", "touchmove", "MSPointerDown", "MSPointerMove"],
    throttle: 500,
    crossTab: true,
    syncTimers: 200,
  });

  useEffect(() => {
    if (remainingTime < showIdleTimerWithSecondsLeft) {
      setShowIdleTimerModal(true);
    } else {
      setShowIdleTimerModal(false);
    }
  }, [remainingTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const handleLogout = async (): Promise<void> => {
    clearLocalSSOStorage();
    await logoutUser();
    setAuthState("signedOut");
    if(loginRedirectUrl) {
      navigate(loginRedirectUrl);
    } else {
      navigateClient("/login");
    }
  };

  return (
    <>
      {showIdleTimerModal && (
        <Modal visible={true} title={translate("AUTO_LOGOUT.sessionTimeout")}>
          <Paragraph>
            <Trans
              i18nKey="AUTO_LOGOUT.youWillBeTimedOut"
              values={{ remainingTime: remainingTime }}
            />
          </Paragraph>
        </Modal>
      )}
    </>
  );
};
